/**
 * Worklist integration helpers for runtime plugins.
 *
 *   <WorklistPluginColumn studyId=X />
 *     Renders stacked badges for every plugin filling the
 *     worklist_row_badge slot. Dropped into a worklist row cell.
 *
 *   useWorklistViewTrigger(studyIds)
 *     Hook called by WorklistPage whenever the active worklist re-renders
 *     (filter applied, local search change, etc). Debounces for 2s, then
 *     fires POST /triage/study/{id} on each plugin+study pair whose rules
 *     yield action='send' at moment='on_worklist_view'.
 *
 *   <ManualBulkSendButton selectedStudyIds=... />
 *     Toolbar button that fires manual triggers for the current selection.
 */

import { useEffect, useRef, useState } from 'react';
import { getPluginResult, triggerPlugin, triggerPluginBulk } from '../client';
import { usePluginsForSlot, usePlugins } from '../hooks';
import { pluginsForTrigger } from '../registry';
import { evaluateRules } from '../rules/evaluator';
import type { Rule, StudyFacts } from '../rules/types';
import { WorklistBadgeSlot } from './WorklistBadgeSlot';
import { writeAudit } from '../audit';

/** Re-exports the slot component so WorklistPage only needs to import this
 *  one module. */
export const WorklistPluginColumn = WorklistBadgeSlot;

/** Hook fired by the worklist whenever its row set changes. Debounces for
 *  `debounceMs` (default 2000) then evaluates each plugin's on_worklist_view
 *  rules per study and dispatches /trigger on the matches.
 *
 *  Caching is implicit: each sidecar returns cached results for study IDs
 *  it's already processed, so re-firing is cheap (no second /api/upload
 *  to the vendor).
 *
 *  Pass stable identifiers for study facts — if the array identity flips
 *  on every render, the debounce will keep resetting and nothing fires.
 */
export function useWorklistViewTrigger(
  studies: StudyFacts[],
  debounceMs = 2000,
) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (studies.length === 0) return;
    timer.current = setTimeout(async () => {
      const plugins = pluginsForTrigger('on_worklist_view');
      if (plugins.length === 0) return;
      for (const plugin of plugins) {
        for (const facts of studies) {
          const key = `${plugin.manifest.id}:${facts.studyId}`;
          if (inFlight.current.has(key)) continue;
          const rules = (plugin.config.rules ?? []) as Rule[];
          const decision = evaluateRules(rules, 'on_worklist_view', facts);
          if (decision.action !== 'send') continue;
          // Skip if we already have a non-pending cached result on the sidecar.
          try {
            const existing = await getPluginResult(plugin, facts.studyId);
            if (existing.status !== 'pending') continue;
          } catch { /* fall through and try trigger anyway */ }
          inFlight.current.add(key);
          void triggerPlugin(plugin, facts.studyId)
            .then((r) => writeAudit(plugin, {
              plugin_id: plugin.manifest.id,
              study_id: facts.studyId,
              moment: 'on_worklist_view',
              action: 'send',
              result_status: r.status,
              latency_ms: r.latency_ms,
              cost: plugin.config.perCallCost,
            }))
            .catch((e) => writeAudit(plugin, {
              plugin_id: plugin.manifest.id,
              study_id: facts.studyId,
              moment: 'on_worklist_view',
              action: 'error',
              error: e instanceof Error ? e.message : String(e),
            }))
            .finally(() => inFlight.current.delete(key));
        }
      }
    }, debounceMs);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [JSON.stringify(studies.map((s) => s.studyId)), debounceMs]);
}

/** Button component. Fires /triage/bulk on every triage-category plugin
 *  for the given selected study IDs. */
export function ManualBulkSendButton({
  selectedStudyIds,
  disabled,
}: {
  selectedStudyIds: string[];
  disabled?: boolean;
}) {
  const plugins = usePlugins().filter(
    (p) => p.loaded && p.manifest.category === 'triage',
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  if (plugins.length === 0) return null;

  async function send() {
    if (selectedStudyIds.length === 0) return;
    setBusy(true);
    setMessage(null);
    try {
      const results = await Promise.all(
        plugins.map((p) => triggerPluginBulk(p, selectedStudyIds)
          .then((r) => ({ p, r }))
          .catch((e) => ({ p, err: e instanceof Error ? e.message : String(e) })),
        ),
      );
      const parts = results.map((x: any) => {
        if (x.err) return `${x.p.manifest.name}: ERR ${x.err}`;
        return `${x.p.manifest.name}: ${x.r.success} ok / ${x.r.skipped} skipped / ${x.r.errors} err`;
      });
      setMessage(parts.join(' · '));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        onClick={send}
        disabled={busy || disabled || selectedStudyIds.length === 0}
        title={plugins.map((p) => p.manifest.name).join(', ')}
      >
        {busy ? 'Sending…' : `Send for AI triage${selectedStudyIds.length > 0 ? ` (${selectedStudyIds.length})` : ''}`}
      </button>
      {message && (
        <span style={{ fontSize: 11, color: '#374151', marginLeft: 8 }}>{message}</span>
      )}
    </>
  );
}

/** Convert a WorklistPage study row into the StudyFacts shape the rules
 *  engine expects. Pulls fields that are known to be on the row object;
 *  rules that reference fields the row doesn't have will no-match. */
export function studyRowToFacts(row: any): StudyFacts {
  return {
    studyId: String(row.studyId),
    modality: row.modality,
    bodyPart: row.bodyPart,
    studyDescription: row.studyDescription,
    institution: row.institution,
    referringPhysician: row.referringPhysician,
    patientAgeYears: parsePatientAge(row.patientAge),
    now: new Date(),
  };
}

function parsePatientAge(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  const m = /^(\d+)([YMWD])?/i.exec(raw);
  if (!m) return undefined;
  const n = parseInt(m[1], 10);
  const unit = (m[2] || 'Y').toUpperCase();
  if (unit === 'Y') return n;
  if (unit === 'M') return Math.floor(n / 12);
  if (unit === 'W') return Math.floor(n / 52);
  if (unit === 'D') return Math.floor(n / 365);
  return n;
}

// Unused import suppressor — usePluginsForSlot is exported transitively.
void usePluginsForSlot;
